import { createFilterOptions } from '@mui/material/Autocomplete'
import { Autocomplete, Box, TextField } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useEffect, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { type FilterOptionsState } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'

import type { AppState } from 'state/index'

import { createBrand } from 'api/farm'

declare module 'components/farm-settings/equipment/make-selector.js' {
  export type MakeSelectorProps = {
    value: BrandOption | null
    onValueChange: Function
    row: any
    onBrandAdded: (brandAdded: boolean) => void
    id?: number | string
    property?: string
    apiRef?: any
    error?: boolean
    onBlur?: () => void
    testId?: string
  }

  type BrandOption = {
    id: number | string
    name: string
    inputValue: string | undefined
  }

  export function MakeSelectorComponent(
    props: MakeSelectorProps,
  ): ReactElement<any, any>
}

export default function MakeSelectorComponent(
  props: MakeSelectorProps,
): ReactElement<any, any> {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const filter = createFilterOptions()

  /*Brands*/
  const brands = useSelector((state) => (state as AppState).brand.brands)
  const [brandOptions, setBrandOptions] = useState<BrandOption[]>([])
  /*end*/

  const {
    value,
    onValueChange,
    row,
    onBrandAdded,
    id,
    property,
    apiRef,
    error,
    onBlur,
    testId,
  } = props

  const [isLoading, setIsLoading] = useState(true)
  const [selectedVal, setSelectedVal] = useState<Partial<BrandOption | null>>({
    id: '',
    name: '',
    inputValue: '',
  })

  /*Brands functions*/
  useEffect(() => {
    const _brandOptions: BrandOption[] = []
    brands.forEach((item) => {
      _brandOptions.push({
        id: item.id,
        name: item.name,
        inputValue: '',
      })
    })
    setBrandOptions(_brandOptions)
    setSelectedVal(value)
    setIsLoading(false)
  }, [brands, value])
  /*End*/

  const handleBrandChange = useCallback(
    (e, newValue?) => {
      const typedName =
        typeof newValue === 'string' ? newValue.trim() : newValue?.inputValue

      if (!typedName) {
        setSelectedVal(newValue)
        onValueChange(newValue)
        return
      }

      const existing = brandOptions.find(
        (brand) => brand.name?.toLowerCase() === typedName.toLowerCase(),
      )

      if (existing) {
        setSelectedVal(existing)
        onValueChange(existing)
        return
      }

      //@ts-ignore
      dispatch(createBrand({ name: typedName })).then((res) => {
        onBrandAdded(true)
        const newBrandOption = { id: res.id, name: res.name, inputValue: '' }
        setBrandOptions([...brandOptions, newBrandOption])
        setSelectedVal(newBrandOption)
        onValueChange(newBrandOption)
      })
    },
    [brandOptions, dispatch, onBrandAdded, onValueChange],
  )

  return isLoading ? (
    <div>{t('loading')}</div>
  ) : (
    <Autocomplete
      sx={{
        display: 'flex',
        height: '100%',
        alignItems: 'center',
        width: '20ch',
        '& .MuiFormControl-root': {
          my: 0,
          width: '100%',
        },
        '& .MuiAutocomplete-inputRoot': {
          alignItems: 'center',
          minHeight: 40,
        },
      }}
      value={selectedVal}
      onChange={(e, newValue) => handleBrandChange(e, newValue)}
      onBlur={onBlur}
      filterOptions={(
        options: Partial<BrandOption>[],
        params: FilterOptionsState<Partial<BrandOption>>,
      ) => {
        const filtered = filter(options, params)
        const { inputValue } = params

        const exists = brandOptions.find(
          (m) => m.name?.toLowerCase() === inputValue?.toLowerCase(),
        )

        if (inputValue !== '' && !exists) {
          filtered.push({
            inputValue,
            name: `${t('add')} "${inputValue}"`,
            id: filtered?.length?.toString(),
          })
        }

        return filtered
      }}
      id="equipment-make-selector"
      options={brandOptions}
      getOptionLabel={(option: BrandOption) => {
        if (option.inputValue) {
          return option.inputValue
        }
        return option.name
      }}
      renderOption={(props, option: BrandOption) => (
        <li
          {...props}
          key={option.id}
          data-testid={
            testId &&
            `${testId}-option-${option.inputValue ? 'add' : option.id}`
          }
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {option.inputValue !== '' ? (
              <Box>
                <FontAwesomeIcon icon={['fas', 'plus']} />
              </Box>
            ) : (
              ''
            )}
            <Box
              sx={{
                ml: 1,
              }}
            >
              {option.name}
            </Box>
          </Box>
        </li>
      )}
      freeSolo
      // isOptionEqualToValue={(option: BrandOption, newValue: BrandOption) =>
      //   option?.id === newValue?.id
      // }
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      renderInput={(params) => (
        <TextField
          {...params}
          error={error}
          fullWidth
          onKeyDown={(event) => {
            if (
              event.key === 'Tab' &&
              id !== undefined &&
              property !== undefined
            ) {
              event.stopPropagation()

              apiRef.current.stopCellEditMode({
                id,
                property,
              })

              apiRef.current.setCellFocus(id, property)
            }
          }}
          label={t('select_make_brand')}
          slotProps={{
            ...params.slotProps,
            htmlInput: {
              ...params.slotProps?.htmlInput,
              'data-testid': testId,
            },
          }}
          size="small"
          variant="outlined"
          placeholder={t('select_make_brand')}
        />
      )}
    />
  )
}
